import { HTTP_INTERCEPTORS, HttpClientModule, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { APP_INITIALIZER, LOCALE_ID, NgModule, SecurityContext } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule } from '@angular/router';
import { NgSelectModule } from '@ng-select/ng-select';

import { ToastrModule } from 'ngx-toastr';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { LoaderComponent } from './shared/component/loader/loader.component';
import { AuthService } from './shared/guards/auth.service';
import { HttpClientInterceptorModule } from './shared/interceptor/http.client.interceptor.module';
import { LoaderInterceptor } from './shared/interceptor/loader-interceptor.service';
import { CustomTranslatorService } from './shared/services/custom.translator.service';
import { HttpService } from './shared/services/http.service';
import { LoaderService } from './shared/services/loader.service';
import { MasterService } from './shared/services/masters.service';
import { ToastrMessageService } from './shared/services/toastr.service';
import { UrlService } from './shared/services/url.service';
import { SharedModule } from './shared/shared.module';

import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { TRANSLATE_HTTP_LOADER_CONFIG, TranslateHttpLoader } from '@ngx-translate/http-loader';
import { MarkdownModule, SANITIZE } from 'ngx-markdown';
import { OktaLoginService } from './shared/guards/okta.login.service';

import { MsalGuardConfiguration, MsalInterceptorConfiguration, MsalModule, MsalService } from '@azure/msal-angular';
import { InteractionType, IPublicClientApplication, PublicClientApplication } from '@azure/msal-browser';
import { environment } from '../environments/environment';
import { LayoutService } from './shared/services/layout.service';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { registerLocaleData } from '@angular/common';

import localeEn from '@angular/common/locales/en-CA'

// AoT requires an exported function for factories
// TranslateHttpLoader (v17+) takes no constructor args - it resolves
// HttpClient and TRANSLATE_HTTP_LOADER_CONFIG via inject() internally.
export function createTranslateLoader() {
    return new TranslateHttpLoader();
}

// Ensure translations are loaded before the app starts, so instant()
// calls (e.g. error toasts) never race the async translation file fetch.
export function initTranslations(translate: TranslateService) {
    return () => firstValueFrom(translate.use('en'));
}

// MSAL Instance configuration
const msalInstance: IPublicClientApplication = new PublicClientApplication({
    auth: {
        clientId: environment.azureLoginConf.clientId,
        authority: environment.azureLoginConf.authority,
        redirectUri: environment.azureLoginConf.redirectUri,
    }
});

// Guard configuration
const guardConfig: MsalGuardConfiguration = {
    interactionType: InteractionType.Redirect,
    authRequest: {
        scopes: ['User.Read'] // Adjust scopes based on permissions
    }
};

// Interceptor configuration
const interceptorConfig: MsalInterceptorConfiguration = {
    interactionType: InteractionType.Redirect,
    protectedResourceMap: new Map([
        // Define the API endpoints that need token injection here
        ['https://graph.microsoft.com/v1.0/me', ['User.Read']],
    ])
};


registerLocaleData(localeEn);

@NgModule({
    declarations: [
        AppComponent,
        LoaderComponent
    ],
    exports: [
        LoaderComponent
    ],
    bootstrap: [AppComponent], imports: [RouterModule,
        FormsModule,
        BrowserModule,
        NgSelectModule,
        AppRoutingModule,
        HttpClientModule,
        BrowserAnimationsModule,
        MatSelectModule,
        MatOptionModule,
        ToastrModule.forRoot({
            preventDuplicates: true
        }),
        // DatepickerModule.forRoot(),
        HttpClientInterceptorModule.forRoot(),
        SharedModule,
        TranslateModule.forRoot({
            fallbackLang: 'en',
            loader: {
                provide: TranslateLoader,
                useFactory: (createTranslateLoader),
                deps: [HttpClient]
            }
        }),
        MarkdownModule.forRoot({
            sanitize: {
                provide: SANITIZE,
                useValue: SecurityContext.NONE
            }
        }),
        MsalModule.forRoot(msalInstance, guardConfig, interceptorConfig)],
    providers: [
        ToastrMessageService,
        CustomTranslatorService,
        HttpService,
        UrlService,
        LoaderService,
        AuthService,
        MasterService,
        MsalService,
        OktaLoginService,
        LayoutService,
        { provide: LOCALE_ID, useValue: 'en-CA' },
        { provide: TRANSLATE_HTTP_LOADER_CONFIG, useValue: { prefix: '/assets/i18n/', suffix: '.json' } },
        { provide: APP_INITIALIZER, useFactory: initTranslations, deps: [TranslateService], multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: LoaderInterceptor, multi: true },
        provideHttpClient(withInterceptorsFromDi())
    ]
})
export class AppModule { }
